import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Search, ShieldCheck, ShieldX, Shield, Monitor, Loader2, Clock, AlertTriangle, Lock, X } from 'lucide-react';
import { toast } from 'sonner';

const Admin2FAUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resetModal, setResetModal] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetting, setResetting] = useState(false);

  const fetchUsers = async (query = '') => {
    setLoading(true);
    try {
      const params = query ? { search: query } : {};
      const res = await api.get('/admin/2fa/users', { params });
      setUsers(res.data.users || []);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSearch = (e) => { e.preventDefault(); fetchUsers(search); };

  const openResetModal = (user) => {
    setResetModal(user);
    setResetPassword('');
    setResetCode('');
  };

  const handleReset2FA = async () => {
    if (!resetPassword || !resetCode) {
      toast.error('Both password and verification code are required');
      return;
    }
    setResetting(true);
    try {
      await api.post(`/admin/2fa/users/${resetModal.id}/reset`, {
        password: resetPassword,
        code: resetCode,
      });
      toast.success(`2FA reset for ${resetModal.email}. User must reconfigure.`);
      setResetModal(null);
      fetchUsers(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset 2FA');
    } finally { setResetting(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-2fa-users-page">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>2FA User Management</h1>
        <p className="text-gray-500 text-sm mt-1">View and manage two-factor authentication for all users</p>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} className="w-full border rounded-lg pl-10 pr-3 py-2.5 text-sm" placeholder="Search by email or name..." data-testid="search-2fa-users" />
        </div>
        <button type="submit" className="px-4 py-2.5 bg-[#1A73E8] text-white rounded-lg text-sm hover:bg-[#1557B0]" data-testid="search-btn">Search</button>
      </form>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#1A73E8]" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>No users found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50 text-left text-sm text-gray-600">
                <th className="px-5 py-3">User</th>
                <th className="px-5 py-3">2FA Status</th>
                <th className="px-5 py-3">Trusted Devices</th>
                <th className="px-5 py-3">Last Login</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-900 text-sm">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                    {user.is_admin && <span className="text-[10px] bg-[#E8F0FE] text-[#1A73E8] px-1.5 py-0.5 rounded font-medium">Admin</span>}
                  </td>
                  <td className="px-5 py-4">
                    {user.totp_enabled ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <ShieldCheck className="w-3.5 h-3.5" /> Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        <ShieldX className="w-3.5 h-3.5" /> Not Set Up
                      </span>
                    )}
                    {user.totp_enabled_at && <p className="text-[10px] text-gray-400 mt-1">Since {new Date(user.totp_enabled_at).toLocaleDateString()}</p>}
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                      <Monitor className="w-3.5 h-3.5" /> {user.trusted_devices_count || 0}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {user.last_login ? (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(user.last_login).toLocaleDateString()} {new Date(user.last_login).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    ) : <span className="text-xs text-gray-400">Never</span>}
                  </td>
                  <td className="px-5 py-4 text-right">
                    {user.totp_enabled && (
                      <button
                        onClick={() => openResetModal(user)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border-2 border-orange-300 text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 font-medium"
                        data-testid={`reset-2fa-${user.id}`}
                      >
                        <AlertTriangle className="w-3.5 h-3.5" /> Reset 2FA
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* High Security Action Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setResetModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()} data-testid="reset-2fa-modal">
            {/* Warning Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold text-sm uppercase tracking-wide">High Security Action</span>
                </div>
                <button onClick={() => setResetModal(null)} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reset 2FA for {resetModal.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{resetModal.email}</p>
                <p className="text-sm text-gray-600 mt-3">
                  This will remove their current authenticator setup and all trusted devices. 
                  The user will need to set up 2FA again on their next login.
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs text-red-700 font-medium flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5" /> Admin verification required to proceed
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Admin Password</label>
                <input
                  type="password" value={resetPassword}
                  onChange={e => setResetPassword(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none"
                  placeholder="Enter your admin password"
                  data-testid="reset-admin-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Authenticator Code (or Recovery Code)</label>
                <input
                  value={resetCode}
                  onChange={e => setResetCode(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-sm text-center tracking-widest font-mono focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none"
                  placeholder="000000"
                  maxLength={12}
                  data-testid="reset-admin-code"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setResetModal(null)} className="flex-1 px-4 py-2.5 border rounded-lg text-sm font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={handleReset2FA}
                  disabled={resetting || !resetPassword || !resetCode}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-reset-2fa-btn"
                >
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                  {resetting ? 'Resetting...' : 'Reset 2FA'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin2FAUsersPage;
